/**
 * StarfieldHolesPostFX — Phaser 4 camera filter that paints a per-hole-seeded
 * twinkling starfield inside every incorrect (non-target) hole. Holes near the
 * ball brighten via a wake-radius proximity check.
 *
 * Architecture mirrors TrippyPostFX exactly: a `BaseFilterShader` RenderNode
 * subclass uploads uniforms, and a `Filters.Controller` subclass holds the
 * per-frame state.
 *
 * Registration in BootScene:
 *   renderer.renderNodes.addNodeConstructor(
 *     'FilterStarfieldHolesPostFX',
 *     FilterStarfieldHolesPostFX as unknown as new (...) => ...
 *   );
 *
 * Usage (StarfieldHolesController):
 *   const ctrl = new StarfieldHolesPostFX(camera);
 *   camera.filters.external.add(ctrl);
 */

import Phaser from 'phaser';


// ---------------------------------------------------------------------------
// Fragment shader (GLSL ES 1.0 — WebGL1 compatible)
// ---------------------------------------------------------------------------
//
// Grid constants are inlined as #defines so the shader doesn't need extra
// uniforms for game geometry. These MUST stay in sync with Playfield.ts.
// If you change CELL_W / CELL_H / HOLE_RADIUS / PLAY_MARGIN_* in Playfield.ts,
// update them here too.
//
const FRAG = /* glsl */ `
// STARFIELDHOLESPOSTFX_FS
#pragma phaserTemplate(shaderName)

#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif

uniform sampler2D uMainSampler;
uniform vec2  uResolution;
uniform float uTimeMs;
uniform float uCameraScrollY;
uniform vec2  uBallPos;                   // world coords
uniform float uWakeRadiusPx;
uniform float uBaseStarBrightness;
uniform float uWakeStarBoost;
uniform float uWakeRimShimmer;
uniform float uTwinkleHz;
uniform float uWarmFraction;
uniform vec3  uStarColorCool;
uniform vec3  uStarColorWarm;

varying vec2 outTexCoord;

#pragma phaserTemplate(fragmentHeader)

// Game grid constants — MUST match Playfield.ts
#define GRID_COLS_F    10.0
#define CELL_W         32.0
#define CELL_H         40.0
#define PLAY_MARGIN_X  20.0
#define PLAY_MARGIN_TOP 60.0
#define HOLE_RADIUS    9.0
#define HOLE_RADIUS_SQ 81.0
#define STARS_PER_HOLE 6

float hash11(float p) {
  p = fract(p * 0.1031);
  p *= p + 33.33;
  p *= p + p;
  return fract(p);
}

vec2 hash22(vec2 p) {
  vec2 q = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));
  return fract(sin(q) * 43758.5453);
}

bool isOnHoleFill(vec3 base) {
  // palette.holeFill = #0E0418 ≈ vec3(0.055, 0.016, 0.094) normalized.
  // Generous tolerance to absorb any sub-pixel blending at hole edges.
  return base.r < 0.10 && base.g < 0.06 && base.b < 0.13;
}

void main () {
  vec4 base = boundedSampler(uMainSampler, outTexCoord);
  // outTexCoord.y is 0 at BOTTOM of the framebuffer, 1 at TOP (WebGL convention).
  // We need screen-y measured from the TOP for the world-coord math.
  vec2 fragPx = vec2(outTexCoord.x * uResolution.x, (1.0 - outTexCoord.y) * uResolution.y);
  float worldY = fragPx.y + uCameraScrollY;

  // Reject pixels outside the hole grid early
  if (worldY < PLAY_MARGIN_TOP - HOLE_RADIUS) { gl_FragColor = base; return; }

  // --- Find candidate row ---------------------------------------------------
  float relY = worldY - PLAY_MARGIN_TOP - CELL_H * 0.5;
  float fRow = floor(relY / CELL_H + 0.5);
  if (fRow < 0.0) { gl_FragColor = base; return; }
  float cellY = PLAY_MARGIN_TOP + CELL_H * 0.5 + fRow * CELL_H;
  float dy = worldY - cellY;
  if (abs(dy) > HOLE_RADIUS) { gl_FragColor = base; return; }

  // --- Find candidate column with stagger ----------------------------------
  float stagger = (mod(fRow, 2.0) > 0.5) ? CELL_W * 0.5 : 0.0;
  float relX = fragPx.x - PLAY_MARGIN_X - CELL_W * 0.5 - stagger;
  float fCol = floor(relX / CELL_W + 0.5);
  if (fCol < 0.0 || fCol > GRID_COLS_F - 1.0) { gl_FragColor = base; return; }
  float cellX = PLAY_MARGIN_X + CELL_W * 0.5 + fCol * CELL_W + stagger;
  float dx = fragPx.x - cellX;
  float distSq = dx * dx + dy * dy;
  if (distSq > HOLE_RADIUS_SQ) { gl_FragColor = base; return; }

  // --- Inside a candidate hole; check skip conditions via cell-center sample
  // Sample the underlying scene at the CELL CENTER (not at the current fragment).
  // If that's on hole-fill, this cell has a real static hole sprite — paint the
  // full cell disc symmetrically. Doing the check at the center, not per-fragment,
  // avoids clipping star halos and rim-shimmer rings at the hole-fill edge.
  float cellScreenY = cellY - uCameraScrollY;
  vec2 cellTexCoord = vec2(cellX / uResolution.x, 1.0 - cellScreenY / uResolution.y);
  vec4 cellCenterSample = boundedSampler(uMainSampler, cellTexCoord);
  if (!isOnHoleFill(cellCenterSample.rgb)) { gl_FragColor = base; return; }
  float cellId = fRow * GRID_COLS_F + fCol;

  // --- Starfield interior ---------------------------------------------------
  vec2 local = vec2(dx, dy) / HOLE_RADIUS;   // -1..1
  float seed = cellId;
  float ballDist = distance(vec2(cellX, cellY), uBallPos);
  float wakeT = 1.0 - smoothstep(uWakeRadiusPx * 0.4, uWakeRadiusPx, ballDist);

  vec3 stars = vec3(0.0);
  for (int s = 0; s < STARS_PER_HOLE; s++) {
    vec2 sp = hash22(vec2(seed, float(s))) * 1.6 - 0.8;    // star inside disc
    float d = distance(local, sp);
    float phase = hash11(seed * 13.0 + float(s)) * 6.283;
    float twinkle = 0.5 + 0.5 * sin(uTimeMs * 0.001 * 6.283 * uTwinkleHz + phase);
    float intensity = (uBaseStarBrightness + uWakeStarBoost * wakeT) * twinkle;
    float starShape = smoothstep(0.15, 0.0, d);
    vec3 tint = (hash11(seed + float(s)) < uWarmFraction)
              ? uStarColorWarm
              : uStarColorCool;
    stars += tint * starShape * intensity;
  }

  // --- Rim shimmer on wake --------------------------------------------------
  float r = length(local);
  float rimBand = smoothstep(0.7, 1.0, r) * (1.0 - smoothstep(1.0, 1.05, r));
  vec3 rim = vec3(0.6, 0.4, 0.9) * rimBand * uWakeRimShimmer * wakeT
           * (0.5 + 0.5 * sin(uTimeMs * 0.004 + seed));

  gl_FragColor = vec4(base.rgb + stars + rim, base.a);
}
`;

// ---------------------------------------------------------------------------
// RenderNode — uniform upload
// ---------------------------------------------------------------------------

const BaseFilterShaderCtor = Phaser.Renderer.WebGL.RenderNodes.BaseFilterShader as unknown as new (
  name: string,
  manager: Phaser.Renderer.WebGL.RenderNodes.RenderNodeManager,
  fragmentShaderKey: string | undefined,
  fragmentShaderSource: string,
  shaderAdditions?: Phaser.Types.Renderer.WebGL.ShaderAdditionConfig[]
) => Phaser.Renderer.WebGL.RenderNodes.BaseFilterShader;

export class FilterStarfieldHolesPostFX extends BaseFilterShaderCtor {
  constructor(manager: Phaser.Renderer.WebGL.RenderNodes.RenderNodeManager) {
    super('FilterStarfieldHolesPostFX', manager, undefined, FRAG);
  }

  setupUniforms(
    controller: Phaser.Filters.Controller,
    _drawingContext: Phaser.Renderer.WebGL.DrawingContext
  ): void {
    const ctrl = controller as StarfieldHolesPostFX;
    const pm = (this as unknown as { programManager: Phaser.Renderer.WebGL.ProgramManager }).programManager;
    // Derive logical viewport from the camera's actual zoom so the shader
    // stays aligned with the rendered scene through any camera-zoom changes.
    pm.setUniform('uResolution', [
      ctrl.camera.width / ctrl.camera.zoomX,
      ctrl.camera.height / ctrl.camera.zoomY,
    ]);
    pm.setUniform('uTimeMs', ctrl.uTimeMs);
    pm.setUniform('uCameraScrollY', ctrl.uCameraScrollY);
    pm.setUniform('uBallPos', ctrl.uBallPos);
    pm.setUniform('uWakeRadiusPx', ctrl.uWakeRadiusPx);
    pm.setUniform('uBaseStarBrightness', ctrl.uBaseStarBrightness);
    pm.setUniform('uWakeStarBoost', ctrl.uWakeStarBoost);
    pm.setUniform('uWakeRimShimmer', ctrl.uWakeRimShimmer);
    pm.setUniform('uTwinkleHz', ctrl.uTwinkleHz);
    pm.setUniform('uWarmFraction', ctrl.uWarmFraction);
    pm.setUniform('uStarColorCool', ctrl.uStarColorCool);
    pm.setUniform('uStarColorWarm', ctrl.uStarColorWarm);
  }
}

// ---------------------------------------------------------------------------
// Controller — per-frame state
// ---------------------------------------------------------------------------

export class StarfieldHolesPostFX extends (
  Phaser.Filters.Controller as unknown as new (
    camera: Phaser.Cameras.Scene2D.Camera,
    renderNode: string
  ) => Phaser.Filters.Controller
) {
  public uTimeMs = 0;
  public uCameraScrollY = 0;
  public uBallPos: [number, number] = [0, 0];
  public uWakeRadiusPx = 80;
  public uBaseStarBrightness = 0.55;
  public uWakeStarBoost = 1.8;
  public uWakeRimShimmer = 0.35;
  public uTwinkleHz = 0.35;
  public uWarmFraction = 0.3;
  public uStarColorCool: [number, number, number] = [0.66, 0.79, 1.0];
  public uStarColorWarm: [number, number, number] = [1.0, 0.88, 0.69];

  constructor(camera: Phaser.Cameras.Scene2D.Camera) {
    super(camera, 'FilterStarfieldHolesPostFX');
  }
}
